import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Navigate, useParams } from "react-router-dom";
import { http } from "../lib/api";
import { ArrowUpRight, CalendarRange, ShieldCheck, FileText, Smartphone, ShieldAlert, Siren, CalendarOff, Package } from "lucide-react";
import { XALTIX_CSS, Emblem, XaltixWelcome } from "../components/XaltixWelcome";
import { SessionReplaced } from "../components/SessionReplaced";
import { KICK_FLAG, LOGIN_PATH_KEY } from "../lib/session";
import { InstallAppButton, useAppManifest } from "../components/InstallApp";

const XALTIX_URL = "https://solutions-prime.fr/xaltix";
const inputCls = "mt-1.5 w-full bg-[#06184A]/60 border border-[#D9A441]/25 text-white placeholder-[#8DA2D6]/60 rounded-lg px-3.5 py-3 text-sm outline-none transition-colors duration-200 focus:border-[#F6D88A] focus:ring-2 focus:ring-[#D9A441]/25";
const FEATURES = [[CalendarRange, "Planning & vacations"], [ShieldCheck, "Conformité CNAPS"], [FileText, "Devis, factures, prépaie"], [Smartphone, "Application agent"], [ShieldAlert, "Main courante électronique"], [Siren, "PTI & rondes"], [CalendarOff, "Absences & congés"], [Package, "Matériel & dotations"]];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const { slug } = useParams();
  useAppManifest({ kind: "admin", slug: slug || "" });
  const [tenant, setTenant] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const welcoming = useRef(false);
  const [kicked, setKicked] = useState(() => { try { return sessionStorage.getItem(KICK_FLAG) === "1"; } catch { return false; } });
  const done = useCallback((to = "/") => { welcoming.current = false; nav(to, { replace: true }); }, [nav]);

  useEffect(() => {
    if (!slug) return;
    http.get(`/public/tenant/${slug}`).then((r) => setTenant(r.data)).catch(() => setTenant(false));
  }, [slug]);

  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet"; l.href = "https://fonts.googleapis.com/css2?family=Michroma&family=Manrope:wght@300;400;500;600;700;800&display=swap";
    document.head.appendChild(l);
    document.title = "XALTIX — Connexion";
    return () => l.remove();
  }, []);

  if (kicked && !user) return <SessionReplaced onDone={() => { try { sessionStorage.removeItem(KICK_FLAG); } catch {} setKicked(false); }} />;
  if (user && welcoming.current) return <XaltixWelcome name={user.name} onDone={done} />;
  if (user && user !== false) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    welcoming.current = true;
    const res = await login(email, password, slug);
    setLoading(false);
    if (!res.ok) { welcoming.current = false; setErr(res.error); }
    else { try { localStorage.setItem(LOGIN_PATH_KEY, slug ? `/e/${slug}` : "/login"); } catch {} }
  };

  return (
    <div className="xl min-h-[100dvh] lg:h-[100dvh] lg:overflow-hidden grid grid-cols-1 lg:grid-cols-5 text-[#C9D6F5]" style={{ background: "linear-gradient(160deg,#0B2A7A 0%,#082063 55%,#06184A 100%)" }}>
      <style>{XALTIX_CSS}</style>
      <div className="lg:col-span-2 flex items-center justify-center px-8 py-6 sm:px-10 relative lg:overflow-y-auto">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(500px circle at 50% 20%, rgba(246,216,138,.12), transparent 65%)" }} />
        <div className="w-full max-w-sm relative">
          <div className="flex flex-col items-center text-center mb-6" data-testid="brand-block">
            <div className="xl-zoom w-28 h-28 sm:w-36 sm:h-36"><Emblem className="w-full h-full" testid="login-xaltix-logo" /></div>
            <div className="xl-h xl-gold text-2xl sm:text-3xl mt-4 pl-[.32em]" data-testid="login-xaltix-name">XALTIX</div>
            {slug && (
              <div data-testid="login-tenant-name" className={`mt-3 text-sm font-semibold ${tenant === false ? "text-rose-200" : "text-[#C9D6F5]"}`}>
                {tenant === false ? "Adresse d'entreprise inconnue" : tenant ? `Espace ${tenant.name}` : "…"}
              </div>
            )}
          </div>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#F6D88A]/80">Email</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#F6D88A]/80">Mot de passe</label>
              <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} />
            </div>
            {err && <div data-testid="login-error" className="text-sm text-rose-200 bg-rose-500/15 border border-rose-400/30 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="login-submit" disabled={loading} className="xl-btn w-full disabled:opacity-70 rounded-full py-3 text-sm active:scale-[0.98]">
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>

          <div className="mt-5 text-xs flex justify-between items-center gap-3 flex-wrap">
            <a href="/postuler" data-testid="apply-link" className="hover:underline font-semibold text-[#C9D6F5]">Vous êtes agent ? <span className="text-[#F6D88A]">Postulez ici</span></a>
            <a href="/forgot-password" data-testid="forgot-password-link" className="hover:underline font-semibold text-[#F6D88A]">Mot de passe oublié ?</a>
          </div>
          <div className="mt-4 flex justify-center"><InstallAppButton dark /></div>

          <div className="mt-6 pt-4 border-t border-[#D9A441]/15 text-center">
            <a href={XALTIX_URL} target="_blank" rel="noopener noreferrer" data-testid="login-xaltix-presentation-link"
               className="group inline-flex items-center gap-1.5 text-sm font-semibold text-[#F6D88A] hover:text-white transition-colors">
              Découvrir la solution XALTIX <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>

      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden border-l border-[#D9A441]/15">
        <div className="absolute inset-0" style={{ background: "radial-gradient(900px circle at 70% 30%, #1A4BC0 0%, transparent 60%)" }} />
        <img src="/xaltix-emblem.png" alt="" aria-hidden className="absolute -right-40 -bottom-40 w-[620px] opacity-[0.07] pointer-events-none" />
        <div className="relative z-10 flex flex-col justify-center px-14 py-10 text-white max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-white/5 border border-[#D9A441]/30 backdrop-blur-md rounded-full px-3 py-1.5 w-fit mb-5">
            <span className="h-2 w-2 rounded-full bg-[#F6D88A] animate-pulse"></span>
            <span className="text-xs font-semibold tracking-wide text-[#F6D88A]">Un produit Solutions PRIME</span>
          </div>
          <h2 className="text-4xl xl:text-5xl font-extrabold leading-[1.08] tracking-tight mb-4">
            Pilotez votre entreprise<br />de sécurité <span className="xl-gold">sans friction</span>.
          </h2>
          <p className="text-[#C9D6F5]/80 text-base max-w-lg">
            Planning, agents, conformité, devis, factures et prépaie réunis dans un seul outil, synchronisé en temps réel.
          </p>
          <div className="mt-8 grid grid-cols-2 gap-3 max-w-lg">
            {FEATURES.map(([I, t]) => (
              <div key={t} className="rounded-xl border border-[#D9A441]/20 bg-[#06184A]/50 backdrop-blur-md px-4 py-3 flex items-center gap-3">
                <I className="h-5 w-5 text-[#F6D88A] shrink-0" /><span className="text-sm font-semibold">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
